import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Checkbox, Form, Input } from "antd"
import {
  P2,
  H5,
  AppDownloadTemplateButton,
  AppFileUpload,
  Button,
} from "components"
import SegmentWrapper from "components/custom/SegmentWrapper"
import DashboardWrapper from "components/new-stuff/ui/DashboardWrapper"
import usePermissions from "hooks/usePermissions"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  paymentSchedulesService,
  PAYMENT_SCHEDULES_ENDPOINTS,
  PAYMENT_SCHEDULES_INSTANCE,
} from "services/paymentSchedulesService"
import { useAppNotificationStore, useAuthStore } from "stores"
import { isPadeDomain, onQueryError } from "utils/utils"

type Props = {}

const BulkUploadCustomPayment = (props: Props) => {
  const { toast } = useAppNotificationStore()
  const [file, setFile] = useState<any>(null)
  const [active, setActive] = useState(false)
  const queryClient = useQueryClient()

  const navigate = useNavigate()
  const { user } = useAuthStore()

  const { isLoading: isAddingFile, mutate: submitFile } = useMutation({
    mutationFn: async (formData: FormData) => {
      const data = await paymentSchedulesService.importCustom(formData)

      return data
    },
    onSuccess: (data) => {
      data?.message && toast.success(data.message)
      navigate(-1)
    },
    onError: onQueryError,
  })

  const [form] = Form.useForm()
  const [description, setDescription] = useState("")
  const [isNameModified, setIsNameModified] = useState(false)

  const [canCreate] = usePermissions({
    allowedPermissions: ["can_create_pay_schedules"],
  })

  const {
    isFetching: isCheckingScheduleName,
    isError: isCheckNameError,
    error: checkNameError,
  } = useQuery({
    queryFn: () => paymentSchedulesService.checkName(description, -1),
    queryKey: [PAYMENT_SCHEDULES_ENDPOINTS.CHECK_NAME, description],
    enabled: !!description,
  })

  const nameError =
    !isNameModified && isCheckNameError
      ? (checkNameError as any)?.response?.data?.message ||
        "Name already in use"
      : ""

  const handleFinish = () => {
    if (isAddingFile) return
    if (file) {
      const formData = new FormData()
      formData.append("file_upload", file)
      formData.append("description", form.getFieldValue("description"))
      submitFile(formData)
    }
  }

  return (
    <DashboardWrapper>
      <SegmentWrapper
        title="Bulk custom payment upload in 3 steps"
        subtitle={
          isPadeDomain() ? (
            <p className="font-normal text-[#42526D] ">
              Need Help?{" "}
              <a
                href="https://support.paidhr.com"
                className=" text-primary500 font-light"
                target="_blank"
              >
                Get Started: Bulk upload custom payment
              </a>
            </p>
          ) : (
            <></>
          )
        }
        contentHeading={
          <div className=" mb-8">
            <h3 className="font-medium text-lg text-[#42526D] mb-2  font-circular">
              What you must have ready
            </h3>
            <div className="flex flex-col w-full gap-3">
              {["Amount to pay"].map((item, index) => (
                <P2 className="flex w-full gap-4">
                  <img
                    src={
                      "https://padefiles.blob.core.windows.net/images/8a82057b-5be2-4992-9e08-0c0f66ca9bc5.svg"
                    }
                  />
                  <span className="font-medium text-sm text-[#5D6B82] ml-4">
                    {item}
                  </span>
                </P2>
              ))}
            </div>
          </div>
        }
      >
        <Form
          form={form}
          onFinish={() => {
            handleFinish()
          }}
          layout="vertical"
        >
          <div className="px-4 md:px-6 xl:px-[60px] mb-6 space-y-10">
            <div className="w-full flex flex-col gap-2.5">
              <H5 className="font-medium text-neutral600">
                1. Download spreadsheet
              </H5>
              <P2 className="text-neutral500">
                We recommend that you download a new spreadsheet before each
                bulk payment schedule upload. This ensures that your lookup data
                is up-to-date.
              </P2>
              <div className="flex gap-2 items-center">
                <Checkbox
                  onChange={(e) => {
                    setActive(e.target.checked)
                  }}
                />{" "}
                <P2 className="font-medium text-neutral600">
                  Include exited employees
                </P2>
              </div>
              <AppDownloadTemplateButton
                fileName={`${user?.userOrganisation}_bulk_custom_payment_schedule`}
                url={`${PAYMENT_SCHEDULES_ENDPOINTS.DOWNLOAD_BULK_CUSTOM_FORM}?include_exited=${active}`}
                method="GET"
                type="xlsx"
                instanceKey={PAYMENT_SCHEDULES_INSTANCE}
              />
            </div>

            <div className="w-full flex flex-col gap-2.5">
              <H5 className="font-medium text-neutral600">
                2. Enter payment schedule title
              </H5>

              <Form.Item
                name="description"
                label="Enter payment schedule title"
                rootClassName="mb-0"
                extra={
                  nameError && (
                    <span className="text-danger500">{nameError}</span>
                  )
                }
                rules={[
                  {
                    required: true,
                    message: "Please add a title",
                  },
                ]}
              >
                <Input
                  onChange={() => {
                    setIsNameModified(true)
                  }}
                  onBlur={(e) => {
                    setIsNameModified(false)
                    e.target.value && setDescription(e.target.value)
                  }}
                />
              </Form.Item>
            </div>

            <div className="w-full flex flex-col gap-2.5">
              <H5 className="font-medium text-neutral600">
                3. Upload spreadsheet
              </H5>
              <P2 className="text-neutral500">
                We recommend that you double-check to be sure you have filled
                the spreadsheet correctly.
                <br /> Note: Maximum allowed file size is 2MB.
              </P2>
              <AppFileUpload
                onUpdate={(file) => {
                  setFile(file)
                }}
              />
            </div>
          </div>

          <div className="flex justify-end items-center w-full pt-6  mt-6 -mb-4 border-t px-4 md:px-6 lg:px-8 xl:px-[60px]">
            <div className={`mt-2 flex gap-3 justify-end `}>
              <Button
                onClick={(e) => {
                  e.preventDefault()
                  navigate(-1)
                }}
                color="neutral"
                type="button"
                className="h-fit text-[#5D6B82]  font-circular"
                disabled={isAddingFile}
              >
                Cancel
              </Button>
              <Button
                title="Upload"
                color="primary"
                className="w-max"
                // disabled={isAddingFile}
                type="submit"
                submitting={isAddingFile}
              >
                Upload information
              </Button>
            </div>
          </div>
        </Form>
      </SegmentWrapper>
    </DashboardWrapper>
  )
}

export default BulkUploadCustomPayment
